import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, addDoc, updateDoc, doc, query, getDocs, Timestamp, getDoc, deleteDoc, writeBatch, where, setDoc } from 'firebase/firestore';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { X, Clock, Calendar, User, ChevronLeft, ChevronRight, Plus, AlertTriangle, Copy, RotateCcw, Search, Shield, Check, XCircle, AlertCircle } from 'lucide-react';
import ClientAutocomplete from './ClientAutocomplete';
import EnhancedAppointmentDetailsModal from './EnhancedAppointmentDetailsModal';
import StandardAddAppointmentModal from './StandardAddAppointmentModal';
import EnhancedEditAppointmentModal from './EnhancedEditAppointmentModal';
import { useTheme } from '../contexts/ThemeContext';
import './ClientWeekView.css';

// Hook personnalisé pour détecter la taille de l'écran
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
}

const WeekView = ({ db, currentDate, currentUser, showCompletedAppointments, filterText }) => {
  const { theme } = useTheme();

  // Vérifier si currentUser est null et retourner un message d'attente
  if (!currentUser || !currentUser.userId) {
    return (
      <div className="week-view flex items-center justify-center h-full bg-white dark:bg-gray-900">
        <div className="text-center text-gray-500 dark:text-gray-400 p-4">
          <p>Chargement des informations utilisateur...</p>
        </div>
      </div>
    );
  }

  const [weekDates, setWeekDates] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [firstDayOfWeek, setFirstDayOfWeek] = useState(1); // Default to Monday (1)
  const [showEditModal, setShowEditModal] = useState(false);
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [unassignedAppointments, setUnassignedAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  // filterText is now a prop
  const [selectedDay, setSelectedDay] = useState(new Date(currentDate));
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRoles, setAdminRoles] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedUnassignedAppointment, setSelectedUnassignedAppointment] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedInfoAppointment, setSelectedInfoAppointment] = useState(null);
  const [clickTimer, setClickTimer] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDetailAppointment, setSelectedDetailAppointment] = useState(null);
  const clickDelay = 200; // Délai en millisecondes pour détecter un double-clic
  const [userPermissions, setUserPermissions] = useState({
    read: false,
    write: false,
    create: false,
    delete: false,
    manage: false
  });
  const [selectedEditAppointment, setSelectedEditAppointment] = useState(null);

  const { width } = useWindowSize();
  const isMobile = width < 768;

  // --- PERMISSIONS & ROLES ---
  const fetchUserPermissions = useCallback(async () => {
    if (!db || !currentUser) return;
    try {
      const userDoc = await getDoc(doc(db, 'employees', currentUser.userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userRoleId = userData.role;

        try {
          const advancedRoleDoc = await getDoc(doc(db, 'advanced_roles', userRoleId));
          if (advancedRoleDoc.exists()) {
            const roleData = advancedRoleDoc.data();
            const appointmentsPerms = roleData.permissions?.appointments || {};
            setUserPermissions({
              read: appointmentsPerms.read || false,
              write: appointmentsPerms.write || false,
              create: appointmentsPerms.create || false,
              delete: appointmentsPerms.delete || false,
              manage: appointmentsPerms.manage || false
            });
            return;
          }
        } catch (e) { }

        const roleDoc = await getDoc(doc(db, 'roles', userRoleId));
        if (roleDoc.exists()) {
          const roleData = roleDoc.data();
          setUserPermissions({
            read: roleData.permissions.includes('read_appointments'),
            write: roleData.permissions.includes('write_appointments'),
            create: roleData.permissions.includes('write_appointments'),
            delete: roleData.permissions.includes('write_appointments'),
            manage: roleData.permissions.includes('write_appointments')
          });
        }
      }
    } catch (error) {
      console.error("Erreur permissions:", error);
    }
  }, [db, currentUser]);

  useEffect(() => { fetchUserPermissions(); }, [fetchUserPermissions]);

  const canViewAllAppointments = useMemo(() => userPermissions.manage && !isMobile, [userPermissions.manage, isMobile]);
  const canModifyAppointments = useMemo(() => userPermissions.write, [userPermissions.write]);
  const canCreateAppointments = useMemo(() => userPermissions.create, [userPermissions.create]);
  const canDeleteAppointments = useMemo(() => userPermissions.delete, [userPermissions.delete]);
  const canManageAppointments = useMemo(() => userPermissions.manage, [userPermissions.manage]);

  const fetchUserRole = useCallback(async () => {
    if (!db || !currentUser) return;
    try {
      const userDoc = await getDoc(doc(db, 'employees', currentUser.userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userRoleId = userData.role;
        setUserRole(userRoleId);
        setIsAdmin(adminRoles.includes(userRoleId));
      }
    } catch (error) { console.error("Erreur role:", error); }
  }, [db, currentUser, adminRoles]);



  // --- DATA FETCHING ---
  const fetchEmployees = useCallback(async () => {
    if (!db) return;
    try {
      const employeesRef = collection(db, 'employees');
      const q = query(employeesRef, where('isArchived', '==', false));

      const [querySnapshot, currentUserDoc] = await Promise.all([
        getDocs(q),
        currentUser ? getDoc(doc(db, 'employees', currentUser.userId)) : Promise.resolve(null)
      ]);

      const fetchedEmployeesMap = new Map();
      querySnapshot.docs.forEach(doc => {
        fetchedEmployeesMap.set(doc.id, {
          userId: doc.id,
          ...doc.data(),
          color: doc.data().color || '#808080',
          isAdmin: adminRoles.includes(doc.data().role)
        });
      });

      if (currentUserDoc && currentUserDoc.exists()) {
        const userData = currentUserDoc.data();
        fetchedEmployeesMap.set(currentUserDoc.id, {
          userId: currentUserDoc.id,
          ...userData,
          color: userData.color || '#808080',
          isAdmin: adminRoles.includes(userData.role)
        });
      }

      const filtered = Array.from(fetchedEmployeesMap.values()).filter(emp => {
        if (currentUser && emp.userId === currentUser.userId) return true;
        if (emp.role === 'beneficiaire') return false;
        return emp.isActive !== false && !emp.isArchived;
      });

      setEmployees(filtered);
    } catch (error) { console.error("Erreur employés:", error); }
  }, [db, adminRoles, currentUser]);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees, adminRoles]);
  useEffect(() => { fetchUserRole(); }, [fetchUserRole, adminRoles]);

  const visibleEmployees = useMemo(() => {
    let baseList = employees;
    if (isMobile) {
      return employees.filter(emp => emp.userId === currentUser.userId);
    }

    // Si pas admin/manager, on ne voit que soi-même (sauf permission explicite)
    if (!isAdmin && !canViewAllAppointments) {
      baseList = employees.filter(emp => emp.userId === currentUser.userId);
    }

    if (!filterText) return baseList;

    const lowerFilter = filterText.toLowerCase();

    return baseList.filter(emp => {
      // 1. Correspondance Nom Employé
      const empName = (emp.name || (emp.firstName && emp.lastName ? `${emp.firstName} ${emp.lastName}` : emp.userId)).toLowerCase();
      if (empName.includes(lowerFilter)) return true;

      // 2. Correspondance Nom Client dans ses rendez-vous
      // On garde la ligne de l'employé si UN de ses rendez-vous correspond au filtre (Client)
      const hasMatchingApp = appointments.some(app =>
        app.employeeId === emp.userId &&
        app.clientName && app.clientName.toLowerCase().includes(lowerFilter)
      );

      return hasMatchingApp;
    });
  }, [employees, isAdmin, currentUser, isMobile, canViewAllAppointments, filterText, appointments]);

  // Settings & Dates
  useEffect(() => {
    const fetchSettings = async () => {
      if (!db) return;
      const docSnap = await getDoc(doc(db, 'settings', 'general'));
      if (docSnap.exists()) setFirstDayOfWeek(docSnap.data().firstDayOfWeek || 1);
    };
    fetchSettings();
  }, [db]);

  const getWeekDates = useCallback((date) => {
    const weekStart = new Date(date);
    const day = weekStart.getDay();
    const diff = (day < firstDayOfWeek ? 7 : 0) + day - firstDayOfWeek;
    weekStart.setDate(weekStart.getDate() - diff);
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      dates.push(d);
    }
    return dates;
  }, [firstDayOfWeek]);

  useEffect(() => {
    setWeekDates(getWeekDates(currentDate));
    setSelectedDay(new Date(currentDate));
  }, [currentDate, getWeekDates]);

  // Appointments
  const fetchAppointments = useCallback(async () => {
    if (!db || weekDates.length === 0) return;
    try {
      const startOfWeek = new Date(weekDates[0]);
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(weekDates[6]);
      endOfWeek.setHours(23, 59, 59, 999);

      const q = query(
        collection(db, 'appointments'),
        where('date', '>=', Timestamp.fromDate(startOfWeek)),
        where('date', '<=', Timestamp.fromDate(endOfWeek))
      );
      const querySnapshot = await getDocs(q);

      const apps = await Promise.all(querySnapshot.docs.map(async d => {
        const data = d.data();
        let clientData = null;
        if (data.clientId) {
          try {
            const cDoc = await getDoc(doc(db, 'clients', data.clientId));
            if (cDoc.exists()) clientData = cDoc.data();
          } catch (e) { }
        }
        return {
          id: d.id,
          ...data,
          date: data.date?.toDate() || new Date(),
          clientName: clientData ? `${clientData.lastName} ${clientData.firstName}` : 'Client Inconnu',
          clientColor: clientData ? clientData.color : '#808080',
          status: data.status || 'libre'
        };
      }));
      setAppointments(apps);
      setUnassignedAppointments(apps.filter(app => !app.employeeId || app.status === 'libre'));
    } catch (e) { console.error(e); }
  }, [db, weekDates]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  // Roles Admin
  useEffect(() => {
    const fetchRoles = async () => {
      if (!db) return;
      const s = await getDocs(collection(db, 'roles'));
      const ids = s.docs.filter(d => d.data().permissions?.includes('admin_access')).map(d => d.id);
      setAdminRoles(ids);
    };
    fetchRoles();
  }, [db]);

  // Clients
  useEffect(() => {
    const fetchClients = async () => {
      if (!db) return;
      const q = query(collection(db, 'clients'), where('isActive', '==', true));
      const s = await getDocs(q);
      setClients(s.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    fetchClients();
  }, [db]);

  // --- ACTIONS ---
  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination || !canModifyAppointments) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const [_, destTimeStr, destEmployeeId] = destination.droppableId.split('-');
    const destDate = new Date(parseInt(destTimeStr));
    const appointment = appointments.find(app => app.id === draggableId);

    try {
      const newEmployeeId = destEmployeeId === 'null' ? null : destEmployeeId;
      const newStatus = newEmployeeId ? 'assigne' : 'libre';
      const newDate = new Date(destDate);
      newDate.setHours(appointment.date.getHours(), appointment.date.getMinutes());

      await updateDoc(doc(db, 'appointments', appointment.id), {
        employeeId: newEmployeeId,
        date: Timestamp.fromDate(newDate),
        status: newStatus
      });
      fetchAppointments();
    } catch (e) { console.error(e); }
  };

  const handleAppointmentClick = (appointment, e) => {
    if (e) e.stopPropagation();
    if (clickTimer) {
      clearTimeout(clickTimer);
      setClickTimer(null);
      if (canModifyAppointments) {
        setSelectedEditAppointment(appointment);
        setShowEditModal(true);
      }
    } else {
      setClickTimer(setTimeout(() => {
        setSelectedDetailAppointment(appointment);
        setShowDetailsModal(true);
        setClickTimer(null);
      }, clickDelay));
    }
  };

  const handleCellClick = (date, employeeId) => {
    if (!canCreateAppointments) return;
    setSelectedDate(date);
    setSelectedEmployee(employeeId);
    setShowAddModal(true);
  };

  // --- RENDER HELPERS ---
  const isTodayDate = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  const isSameDay = (d1, d2) => {
    return d1.getDate() === d2.getDate() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getFullYear() === d2.getFullYear();
  };

  const getStyle = (status, clientColor) => {
    const base = "p-1.5 mb-1.5 rounded text-xs border-l-4 shadow-sm cursor-pointer transition-all hover:brightness-95 hover:shadow-md truncate";
    if (status === 'assigne') return `${base} text-gray-900 dark:text-gray-100 bg-opacity-20 dark:bg-opacity-30`;
    switch (status) {
      case 'libre': return `${base} bg-red-100 dark:bg-red-900/30 border-red-500 text-red-800 dark:text-red-200`;
      case 'reserve': return `${base} bg-amber-100 dark:bg-amber-900/30 border-amber-500 text-amber-800 dark:text-amber-200`;
      case 'completed': return `${base} bg-gray-100 dark:bg-gray-800 border-gray-500 text-gray-600 dark:text-gray-400 opacity-75`;
      case 'in_progress': return `${base} bg-blue-100 dark:bg-blue-900/30 border-blue-500 text-blue-800 dark:text-blue-200`;
      default: return `${base} bg-gray-100 dark:bg-gray-800 border-gray-300 text-gray-900 dark:text-gray-100`;
    }
  };

  const getCustomStyle = (status, clientColor) => {
    if (status === 'assigne') {
      // En mode sombre, on veut peut-être une couleur plus opaque ou différente
      // Mais pour l'instant, on s'assure juste que le texte est lisible (via classes)
      return {
        backgroundColor: clientColor ? `${clientColor}25` : undefined, // Slightly more opaque
        borderLeftColor: clientColor || '#9ca3af'
      };
    }
    return {};
  };

  const isTodayCustom = isTodayDate;

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* HEADER: Jours */}
      <div className="flex flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm z-20">
        <div className="w-48 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 p-3 flex items-center justify-center font-bold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800">
          <User size={16} className="mr-2" /> Intervenants
        </div>
        <div className="flex-1 flex">
          {weekDates.map(date => {
            const current = isTodayDate(date);
            return (
              <div key={date.toString()} className={`flex-1 text-center p-2 border-r border-gray-200 dark:border-gray-700 ${current ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}>
                <div className={`text-xs font-bold uppercase tracking-wider ${current ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`}>
                  {date.toLocaleDateString('fr-FR', { weekday: 'short' })}
                </div>
                <div className={`text-xl font-bold mt-1 ${current ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200'}`}>
                  {date.getDate()}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* BODY: Grille */}
      <div className="flex-1 overflow-y-auto">
        <DragDropContext onDragEnd={onDragEnd}>
          {visibleEmployees.map(employee => (
            <div key={employee.userId} className="flex border-b border-gray-200 dark:border-gray-700 min-h-[140px] bg-white dark:bg-gray-900">
              {/* Colonne Employé */}
              <div className="w-48 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800 sticky left-0 z-10 flex flex-col justify-center">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-sm" style={{ backgroundColor: employee.color || '#808080' }}>
                    {employee.firstName?.[0]}{employee.lastName?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 dark:text-gray-100 truncate text-sm" title={employee.name || `${employee.firstName} ${employee.lastName}`}>
                      {employee.name || `${employee.firstName} ${employee.lastName}`}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {employee.roleName || 'Intervenant'}
                    </div>
                  </div>
                </div>
                {employee.isAdmin && (
                  <div className="mt-2 inline-flex items-center text-[10px] bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-full w-fit">
                    <Shield size={10} className="mr-1" /> Admin
                  </div>
                )}
              </div>

              {/* Cellules Jours */}
              <div className="flex-1 flex bg-white dark:bg-gray-900">
                {weekDates.map(date => {
                  const dateKey = `day-${date.getTime()}-${employee.userId}`;
                  const current = isTodayDate(date);
                  const dayApps = appointments.filter(app => {
                    const matchesEmployee = app.employeeId === employee.userId;
                    const matchesDate = app.date && isSameDay(app.date, date);
                    const isCompleted = app.status === 'completed';
                    const matchesCompleted = showCompletedAppointments || !isCompleted;

                    if (!matchesEmployee || !matchesDate || !matchesCompleted) return false;

                    if (filterText) {
                      const lowerFilter = filterText.toLowerCase();
                      const clientName = app.clientName ? app.clientName.toLowerCase() : '';
                      const empName = (employee.name || `${employee.firstName} ${employee.lastName}` || '').toLowerCase();

                      // Si on cherche "Dupont" (client), on affiche le RDV.
                      // Si on cherche "Paul" (employé), on affiche TOUS les RDV de Paul.
                      return clientName.includes(lowerFilter) || empName.includes(lowerFilter);
                    }

                    return true;
                  });
                  dayApps.sort((a, b) => a.startTime.localeCompare(b.startTime));

                  return (
                    <Droppable key={dateKey} droppableId={dateKey}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`flex-1 border-r border-gray-100 dark:border-gray-800 p-1.5 transition-colors duration-200
                                              ${snapshot.isDraggingOver ? 'bg-blue-50 dark:bg-blue-900/20 ring-inset ring-2 ring-blue-400/50' : ''}
                                              ${current ? 'bg-gray-50/80 dark:bg-gray-800/20' : ''}
                                              hover:bg-gray-50 dark:hover:bg-gray-800
                                          `}
                          onClick={() => handleCellClick(date, employee.userId)}
                          style={{ minWidth: '120px' }}
                        >
                          <div className="flex flex-col gap-1.5 h-full">
                            {dayApps.map((app, idx) => (
                              <Draggable key={app.id} draggableId={app.id} index={idx} isDragDisabled={!canModifyAppointments}>
                                {(providedDrag, snapshotDrag) => (
                                  <div
                                    ref={providedDrag.innerRef}
                                    {...providedDrag.draggableProps}
                                    {...providedDrag.dragHandleProps}
                                    className={`${getStyle(app.status, app.clientColor)} ${snapshotDrag.isDragging ? 'opacity-50 scale-105 shadow-xl rotate-1' : ''}`}
                                    style={{ ...providedDrag.draggableProps.style, ...getCustomStyle(app.status, app.clientColor) }}
                                    onClick={(e) => handleAppointmentClick(app, e)}
                                  >
                                    <div className="flex justify-between items-center text-[10px] font-bold opacity-80 mb-0.5 tracking-tight">
                                      <span className="flex items-center"><Clock size={10} className="mr-0.5" />{app.startTime}-{app.endTime}</span>
                                      {app.status === 'completed' && <Check size={10} />}
                                    </div>
                                    <div className="font-semibold truncate text-[11px] leading-snug">
                                      {app.clientName}
                                    </div>
                                    {app.details && <div className="text-[10px] truncate opacity-70 mt-0.5 italic">{app.details}</div>}
                                  </div>
                                )}
                              </Draggable>
                            ))}
                          </div>
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  );
                })}
              </div>
            </div>
          ))}
        </DragDropContext>
      </div>

      {/* MODALS */}
      {showDetailsModal && selectedDetailAppointment && (
        <EnhancedAppointmentDetailsModal
          appointment={selectedDetailAppointment}
          onClose={() => setShowDetailsModal(false)}
          onUpdate={() => fetchAppointments()}
          onEdit={() => {
            setSelectedEditAppointment(selectedDetailAppointment);
            setShowEditModal(true);
            setShowDetailsModal(false);
          }}
          onDelete={async (id) => {
            try {
              await deleteDoc(doc(db, 'appointments', id));
              fetchAppointments();
              setShowDetailsModal(false);
            } catch (e) { console.error(e); }
          }}
          canEdit={canModifyAppointments}
          db={db}
        />
      )}

      {showAddModal && (
        <StandardAddAppointmentModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAdd={async (newApp) => {
            try {
              await addDoc(collection(db, 'appointments'), {
                ...newApp,
                date: Timestamp.fromDate(newApp.date),
              });
              fetchAppointments();
              setShowAddModal(false);
            } catch (e) { console.error(e); }
          }}
          prefilledDate={selectedDate}
          prefilledEmployeeId={selectedEmployee}
          employees={employees}
          clients={clients}
        />
      )}

      {showEditModal && selectedEditAppointment && (
        <EnhancedEditAppointmentModal
          appointment={selectedEditAppointment}
          employees={employees}
          clients={clients}
          onSave={async (updatedApp) => {
            try {
              await updateDoc(doc(db, 'appointments', selectedEditAppointment.id), {
                ...updatedApp,
                date: Timestamp.fromDate(updatedApp.date)
              });
              fetchAppointments();
              setShowEditModal(false);
            } catch (e) { console.error(e); }
          }}
          onClose={() => setShowEditModal(false)}
          onDelete={async (id) => {
            try {
              await deleteDoc(doc(db, 'appointments', id));
              fetchAppointments();
              setShowEditModal(false);
            } catch (e) { console.error(e); }
          }}
          canDelete={canModifyAppointments}
        />
      )}
    </div>
  );
};

export default WeekView;
